import { AddGroupsPresenter } from "./AddGroupsPresenter";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useCallback, useEffect, useState } from "react";
import {
  CreateRecorderGroupRequest,
  RecorderGroup,
} from "api/interfaces/recorderInterface.interface";
import {
  deleteRecorderGroup,
  postRecorderGroup,
  putRecorderGroup,
} from "api/recorderAPI";
import { checkWhiteSpaceRegEx } from "utils/regEx";
import {
  CustomError,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  onModal: () => void;
  selectGroup?: RecorderGroup;
  isEditModal?: boolean;
  onSuccessDelete?: () => void;
}

export enum UPDATE_TYPE {
  NAME,
  DESC,
  RECORDER,
}

function AddGroupsContainer(props: Props): JSX.Element {
  const auth = useAuth();
  const { handleErrorNotify, lang, notifySuccess, notifyError, intlString } =
    useApiError();

  const [isConfirm, SetIsConfirm] = useState<boolean>(false);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const extreactRecorderId = (systems: { mergedSystemId: string }[]) => {
    return systems.map((recorder) => recorder.mergedSystemId);
  };

  const [recorderGroupValidate, setRecorderGroupValidate] = useState<Validate>({
    isRecorderGroupName: false,
    isRecorderId: false,
  });
  const [recorderGroupValidateMsg, setRecorderGroupValidateMsg] =
    useState<ValidateMsg>({
      isRecorderGroupName: "",
      isRecorderId: "",
    });

  const [recorderGroup, setRecorderGroup] = useState<
    CreateRecorderGroupRequest & { hostValidation?: boolean }
  >({
    name: "",
    description: "",
    mergedSystemIds: [],
    userId: undefined,
  });

  const mutationCreateRecorderGroup = useStatusMutation(postRecorderGroup, {
    onSuccess: () => {
      notifySuccess(lang.label.recorderGroup.notify.create.success);
      props.onModal();
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.recorderGroup.notify.create.fail);
    },
  });

  const initRecorderGroupValidate = useCallback((field: string) => {
    setRecorderGroupValidate((info) => {
      return { ...info, [field as keyof typeof recorderGroupValidate]: false };
    });
    setRecorderGroupValidateMsg((info) => {
      return { ...info, [field as keyof typeof recorderGroupValidate]: "" };
    });
  }, []);

  const onClickCreate = useCallback(() => {
    if (!mutationCreateRecorderGroup.idle) return;
    if (!onBeforeSave()) return;

    mutationCreateRecorderGroup.mutate({
      payload: {
        ...recorderGroup,
        name: toTrim(recorderGroup.name),
      },
      accountId: selectedAccount.accountId,
    });
  }, [mutationCreateRecorderGroup, recorderGroup, selectedAccount.accountId]);

  const onChangeRecorderGroupInfo = useCallback(
    (type: UPDATE_TYPE, value: string | string[]) => {
      if (type === UPDATE_TYPE.RECORDER) {
        setRecorderGroup((info) => {
          return { ...info, mergedSystemIds: value as string[] };
        });
      } else if (type === UPDATE_TYPE.NAME) {
        setRecorderGroup((info) => {
          return { ...info, name: value as string };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setRecorderGroup((info) => {
          return { ...info, description: value as string };
        });
      }
    },
    []
  );

  const setIsDeleteConfirm = (confirm: boolean) => {
    SetIsConfirm(confirm);
  };
  const onDelete = (value: boolean) => {
    SetIsConfirm(value);
  };

  const mutationDeleteRecorderGroup = useStatusMutation(deleteRecorderGroup, {
    onSuccess: () => {
      notifySuccess(lang.label.recorderGroup.notify.delete.success);
      props.onModal();
      props.onSuccessDelete && props.onSuccessDelete();
    },
    onError: () => {
      notifyError(lang.label.recorderGroup.notify.delete.fail);
    },
  });

  const onConfirmDelete = useCallback(() => {
    if (!mutationDeleteRecorderGroup.idle) return;
    mutationDeleteRecorderGroup.mutate({
      recorderGroupId: props.selectGroup?.recorderGroupId as number,
      accountId: selectedAccount.accountId,
    });
  }, [
    mutationDeleteRecorderGroup,
    props.selectGroup?.recorderGroupId,
    selectedAccount.accountId,
  ]);

  const mutationPutRecorderGroup = useStatusMutation(putRecorderGroup, {
    onSuccess: (res: any) => {
      if (res.error === 0) {
        notifySuccess(lang.label.recorderGroup.notify.update.success);
        props.onModal();
      } else {
        notifyError(lang.label.recorderGroup.notify.update.fail);
      }
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.recorderGroup.notify.update.fail);
    },
  });

  const onBeforeSave = useCallback(() => {
    if (
      checkEmpty(recorderGroup.name) ||
      checkWhiteSpaceRegEx(toTrim(recorderGroup.name))
    ) {
      setRecorderGroupValidate((info) => {
        return { ...info, isRecorderGroupName: true as boolean };
      });

      setRecorderGroupValidateMsg((info) => {
        return {
          ...info,
          isRecorderGroupName: intlString(lang.validateMsg.recorderGroup.name),
        };
      });

      return false;
    }

    if (recorderGroup.mergedSystemIds.length === 0) {
      setRecorderGroupValidate((info) => {
        return { ...info, mergedSystemIds: true as boolean };
      });

      setRecorderGroupValidateMsg((info) => {
        return {
          ...info,
          mergedSystemIds: intlString(lang.validateMsg.userGroup.recorder),
        };
      });
      return false;
    }
    return true;
  }, [recorderGroup.mergedSystemIds.length, recorderGroup.name]);

  const onClickUpdate = useCallback(() => {
    if (!mutationPutRecorderGroup.idle) return;
    if (!onBeforeSave()) return;

    mutationPutRecorderGroup.mutate({
      payload: {
        ...recorderGroup,
        name: toTrim(recorderGroup.name),
      },
      accountId: selectedAccount.accountId,
      recorderGroupId: props.selectGroup?.recorderGroupId as number,
    });
  }, [
    mutationPutRecorderGroup,
    onBeforeSave,
    props.selectGroup?.recorderGroupId,
    recorderGroup,
    selectedAccount.accountId,
  ]);

  useEffect(() => {
    if (!mutationCreateRecorderGroup.idle) return;
    if (!mutationPutRecorderGroup.idle) return;
    if (!mutationDeleteRecorderGroup.idle) return;
    if (props.isEditModal) {
      setRecorderGroup({
        name: props.selectGroup !== undefined ? props.selectGroup.name : "",
        description:
          props.selectGroup !== undefined ? props.selectGroup.description : "",
        mergedSystemIds:
          props.selectGroup !== undefined &&
          props.selectGroup.systems !== undefined
            ? extreactRecorderId(props.selectGroup.systems)
            : [],
        userId:
          props.selectGroup !== undefined
            ? props.selectGroup.userId
            : undefined,
        hostValidation: props.selectGroup?.hostValidation,
      });
    }
  }, [
    props.selectGroup,
    props.isEditModal,
    mutationCreateRecorderGroup.idle,
    mutationPutRecorderGroup.idle,
    mutationDeleteRecorderGroup.idle,
  ]);
  return (
    <AddGroupsPresenter
      onModal={props.onModal}
      accountId={selectedAccount.accountId}
      onChangeRecorderGroupInfo={onChangeRecorderGroupInfo}
      onClickCreate={onClickCreate}
      selectData={recorderGroup}
      isEditModal={props.isEditModal}
      setIsDeleteConfirm={setIsDeleteConfirm}
      isConfirm={isConfirm}
      onDelete={onDelete}
      onConfirmDelete={onConfirmDelete}
      onClickUpdate={onClickUpdate}
      recorderGroupValidate={recorderGroupValidate}
      recorderGroupValidateMsg={recorderGroupValidateMsg}
      initRecorderGroupValidate={initRecorderGroupValidate}
      userId={auth.user.userId}
      disabled={
        !mutationCreateRecorderGroup.idle || !mutationPutRecorderGroup.idle
      }
      disabledDelete={!mutationDeleteRecorderGroup.idle}
    />
  );
}

export default AddGroupsContainer;
